import { lazy, Suspense, type ComponentType, type ReactNode } from "react"
import type { RouteObject } from "react-router"

import { preloadedLazy } from "@/app/preloaded-lazy"
import { LoadingState } from "@/components/kit/states"
import { RedirectIfSignedIn, RequireSignedIn } from "./components/AuthGuards"

interface ScreenModule {
  default: ComponentType
}

/** A screen chunk plus the `auth` namespace (its own chunk, ./i18n.ts), registered before it renders. */
const withNamespace = (load: () => Promise<ScreenModule>) => async (): Promise<ScreenModule> => {
  const [screen, ns] = await Promise.all([load(), import("./i18n")])
  await ns.registerAuthNamespace()
  return screen
}

/** Sign-in is the anonymous landing: its chunk starts with the route table (NFR-DSH-01). */
const SignInScreen = preloadedLazy(withNamespace(() => import("./screens/SignInScreen")))
const TotpChallengeScreen = lazy(withNamespace(() => import("./screens/TotpChallengeScreen")))
const RegisterScreen = lazy(withNamespace(() => import("./screens/RegisterScreen")))
const ForgotPasswordScreen = lazy(withNamespace(() => import("./screens/ForgotPasswordScreen")))
const ResetPasswordScreen = lazy(withNamespace(() => import("./screens/ResetPasswordScreen")))
const VerifyEmailScreen = lazy(withNamespace(() => import("./screens/VerifyEmailScreen")))
const AcceptInviteScreen = lazy(withNamespace(() => import("./screens/AcceptInviteScreen")))
const UnsubscribeScreen = lazy(withNamespace(() => import("./screens/UnsubscribeScreen")))
const EnrolTotpScreen = lazy(withNamespace(() => import("./screens/EnrolTotpScreen")))

const screen = (Screen: ComponentType, guard?: (children: ReactNode) => ReactNode) => {
  const element = (
    <Suspense fallback={<LoadingState variant="form" rows={2} />}>
      <Screen />
    </Suspense>
  )
  return guard ? guard(element) : element
}
const visitorsOnly = (children: ReactNode) => <RedirectIfSignedIn>{children}</RedirectIfSignedIn>
const signedInOnly = (children: ReactNode) => <RequireSignedIn>{children}</RequireSignedIn>

/**
 * The public routes (LLD §7.2, PDD §6.2), rendered in `AuthLayout`. Every one
 * is phone-safe. `/register` always exists in the table and renders the
 * not-found screen itself when the deployment has no registration
 * (standalone, or closed), since the mode is known only once `getConfig`
 * answers (the router is built before it, until Task 2.5).
 *
 * The token-bearing links (`/verify-email`, `/accept-invite`) are not
 * redirected for a signed-in user: the link may be someone else's, and its
 * answer says so. `/enrol-totp` needs a session.
 */
export const authRoutes: RouteObject[] = [
  { path: "/sign-in", element: screen(SignInScreen, visitorsOnly) },
  { path: "/sign-in/totp", element: screen(TotpChallengeScreen, visitorsOnly) },
  { path: "/register", element: screen(RegisterScreen, visitorsOnly) },
  { path: "/forgot-password", element: screen(ForgotPasswordScreen, visitorsOnly) },
  { path: "/reset-password", element: screen(ResetPasswordScreen, visitorsOnly) },
  { path: "/verify-email", element: screen(VerifyEmailScreen) },
  { path: "/accept-invite", element: screen(AcceptInviteScreen) },
  { path: "/email/unsubscribe", element: screen(UnsubscribeScreen) },
  { path: "/enrol-totp", element: screen(EnrolTotpScreen, signedInOnly) },
]
